import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal/Modal';
import { ExternalLinkIcon, ArrowLeftIcon, ArrowRightIcon } from '../Icons/Icons';
import { useEvaluationStore } from '../../store/evaluationStore';
import { Button } from '../ui/Button/Button';
import { PdfViewer } from '../PdfViewer';
import './PreviewModal.css';

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  title: string;
  productIndex?: number;
  onNavigate?: (direction: 'prev' | 'next') => void;
}

export const PreviewModal: React.FC<PreviewModalProps> = ({
  isOpen,
  onClose,
  url,
  title,
  productIndex,
  onNavigate
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const { products, updateProduct } = useEvaluationStore();
  const [comment, setComment] = React.useState('');
  const [blobType, setBlobType] = React.useState<string>('');
  
  // ナビゲーション可能かチェック
  const canNavigatePrev = React.useMemo(() => {
    if (productIndex === undefined || !onNavigate) return false;
    for (let i = productIndex - 1; i >= 0; i--) {
      if (products[i]?.reviewLink) return true;
    }
    return false;
  }, [productIndex, products, onNavigate]);
  
  const canNavigateNext = React.useMemo(() => {
    if (productIndex === undefined || !onNavigate) return false;
    for (let i = productIndex + 1; i < products.length; i++) {
      if (products[i]?.reviewLink) return true;
    }
    return false;
  }, [productIndex, products, onNavigate]);

  // キーボードイベントのリスナー
  React.useEffect(() => {
    if (!isOpen || !onNavigate) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && canNavigatePrev && (!e.target || !(e.target as HTMLElement).matches('textarea'))) {
        e.preventDefault();
        onNavigate('prev');
      } else if (e.key === 'ArrowRight' && canNavigateNext && (!e.target || !(e.target as HTMLElement).matches('textarea'))) {
        e.preventDefault();
        onNavigate('next');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, canNavigatePrev, canNavigateNext, onNavigate, onClose]);
  
  // 製品情報からコメントを取得
  React.useEffect(() => {
    if (productIndex !== undefined && products[productIndex]) {
      setComment(products[productIndex].reviewComment || '');
    }
  }, [productIndex, products]);

  const handleOpenInNewTab = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // コメントの保存
  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newComment = e.target.value;
    setComment(newComment);
    
    // productIndexが指定されている場合のみ保存
    if (productIndex !== undefined) {
      updateProduct(productIndex, { reviewComment: newComment });
    }
  };

  // URLの妥当性をチェック
  const isValidUrl = React.useMemo(() => {
    try {
      const urlObj = new URL(url);
      // blob URL、http、httpsを許可
      return ['http:', 'https:', 'blob:'].includes(urlObj.protocol);
    } catch {
      return false;
    }
  }, [url]);

  const handleIframeLoad = () => {
    setLoading(false);
    setError(false);
  };

  const handleIframeError = (event: any) => {
    console.error('iframe読み込みエラー:', event);
    setLoading(false);
    setError(true);
    setErrorMessage(t('preview.loadErrorDetail'));
  };

  // iframeの読み込みタイムアウト設定
  React.useEffect(() => {
    if (isOpen && loading) {
      const timer = setTimeout(() => {
        setLoading(false);
        // タイムアウトしてもエラーとは限らないので、エラー状態は設定しない
      }, 10000); // 10秒でタイムアウト

      return () => clearTimeout(timer);
    }
  }, [isOpen, loading]);

  // モーダルが開かれるたびにリセット
  React.useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setError(false);
      setErrorMessage('');
      setBlobType('');
      
      // blob URLの場合、MIMEタイプを取得
      if (url.startsWith('blob:')) {
        fetch(url)
          .then(response => response.blob())
          .then(blob => {
            setBlobType(blob.type);
            // PDFの場合は特別な処理が必要
            if (blob.type === 'application/pdf') {
              setLoading(false);
            }
          })
          .catch(err => {
            console.error('Failed to fetch blob:', err);
            setError(true);
            setErrorMessage('ファイルの読み込みに失敗しました');
            setLoading(false);
          });
      }
    }
  }, [isOpen, url]);

  if (!isValidUrl) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t('preview.title', { title })}
        size="lg"
      >
        <div className="preview-error">
          <div className="preview-error__icon">⚠️</div>
          <div className="preview-error__message">{t('preview.invalidUrl')}</div>
          <div className="preview-error__detail">
            {t('preview.invalidUrlDetail')}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('preview.title', { title })}
      size="xl"
      headerActions={
        <div className="preview-modal__actions">
          {onNavigate && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onNavigate('prev')}
                disabled={!canNavigatePrev}
                leftIcon={<ArrowLeftIcon />}
              >
                {t('common.prev')}
              </Button>
              <span className="preview-modal__counter">
                {productIndex !== undefined && (() => {
                  const productsWithLinks = products.map((p, idx) => ({ ...p, originalIndex: idx })).filter(p => p.reviewLink);
                  const currentPosition = productsWithLinks.findIndex(p => p.originalIndex === productIndex) + 1;
                  return `${currentPosition} / ${productsWithLinks.length}`;
                })()}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onNavigate('next')}
                disabled={!canNavigateNext}
                rightIcon={<ArrowRightIcon />}
              >
                {t('common.next')}
              </Button>
              <div className="preview-modal__divider" />
            </>
          )}
          <button
            className="icon-button icon-button--link"
            onClick={handleOpenInNewTab}
            title={t('common.openInNewTab')}
            aria-label={t('common.openInNewTab')}
          >
            <ExternalLinkIcon />
          </button>
        </div>
      }
    >
      {loading && (
        <div className="preview-error">
          <div className="preview-error__icon">⏳</div>
          <div className="preview-error__message">{t('preview.loading')}</div>
        </div>
      )}
      
      {error && (
        <div className="preview-error">
          <div className="preview-error__icon">❌</div>
          <div className="preview-error__message">{t('preview.loadError')}</div>
          <div className="preview-error__detail">
            {errorMessage || t('preview.pageLoadError')}
          </div>
          <div className="preview-error__actions">
            <button
              className="button button--primary"
              onClick={handleOpenInNewTab}
            >
              {t('common.openInNewTab')}
            </button>
          </div>
        </div>
      )}

      <div className="preview-modal-body">
        <div className="preview-iframe-wrapper" style={{ display: loading || error ? 'none' : 'block' }}>
          <div className="preview-iframe-container">
            {/* blob URLの場合は、ファイルタイプによって処理を分ける */}
            {url.startsWith('blob:') ? (
              // blob URLの場合は、iframe内で開けない可能性があるため、埋め込みで表示
              blobType === 'application/pdf' ? (
                // PDFの場合、PDF.jsを使用
                <PdfViewer
                  url={url}
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                />
              ) : blobType.startsWith('image/') ? (
                // 画像の場合
                <img
                  src={url}
                  alt={title}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                />
              ) : (
                // その他のblob URLは表示不可
                <div className="preview-error">
                  <div className="preview-error__icon">📄</div>
                  <div className="preview-error__message">
                    このファイルタイプはプレビューできません
                  </div>
                  <div className="preview-error__actions">
                    <button
                      className="button button--primary"
                      onClick={handleOpenInNewTab}
                    >
                      {t('common.openInNewTab')}
                    </button>
                  </div>
                </div>
              )
            ) : (
              // 通常のURLはiframe
              <iframe
                src={url}
                title={t('preview.title', { title })}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-downloads"
                allow="accelerometer; autoplay; camera; encrypted-media; fullscreen; geolocation; gyroscope; magnetometer; microphone; midi; payment; picture-in-picture; publickey-credentials-get; usb; web-share"
                referrerPolicy="no-referrer-when-downgrade"
              />
            )}
          </div>
        </div>

        {/* コメント欄は常に表示 */}
        {productIndex !== undefined && (
          <div className="preview-comment-section">
            <label className="preview-comment-label">
              {t('preview.comment') || 'コメント'}
            </label>
            <textarea
              className="preview-comment-textarea"
              value={comment}
              onChange={handleCommentChange}
              placeholder={t('preview.commentPlaceholder') || 'この評価対象に関するコメントを入力...'}
              rows={3}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
